import { Validators } from '@angular/forms';
import { CustomValidatorsFactory } from '../shared/validators/custom-validators-factory';
import {
  UserInfoDTO,
  CreateUserDTO,
  UpdateUserInfoDTO,
  SortDirection,
  Role
} from '../models/models.swagger.generated';
import { defineDynamicEntityDefinition, textColumn, enumColumn, booleanColumn } from '../shared/dynamic/dynamic-entity-definition';
import { ClientService } from '../services/client/client.service';
import { UserType } from '../models/user-management-data';

// Extend UpdateUserInfoDTO with the form-only fields:
// - `role`: the form edits a single role, while the DTO carries a `roles` array
// - `userType`: dynamically added to the form to drive web/mobile-specific behavior
type UserEditModel = UpdateUserInfoDTO & {
  role?: Role;
  userType?: UserType;
};

export const userManagementDynamicDefinition = defineDynamicEntityDefinition<UserInfoDTO, CreateUserDTO, UserEditModel>({
  entityName: 'user',
  primaryKey: 'userId',
  activeField: 'enabled',
  defaultSort: {
    field: 'firstname',
    direction: SortDirection.Ascending,
  },
  grid: {
    searchFields: ['firstname', 'lastname', 'client.name', 'email'],
    columns: [
      { field: 'userId', headerKey: 'i18n.base.user-management.column-headers.id', type: 'string', visible: false, allowFiltering: false },
      textColumn<UserInfoDTO>('firstname', 'i18n.base.user-management.column-headers.firstName'),
      textColumn<UserInfoDTO>('lastname', 'i18n.base.user-management.column-headers.lastName'),
      textColumn<UserInfoDTO>('client.name', 'i18n.base.user-management.column-headers.client'),
      textColumn<UserInfoDTO>('email', 'i18n.base.user-management.column-headers.email'),
      {
        field: 'language',
        headerKey: 'i18n.base.user-management.column-headers.language',
        type: 'string',
        clipMode: 'EllipsisWithTooltip',
        filter: {
          kind: 'enum',
          options: [
            { value: 'de', textKey: 'i18n.base.shared.Language.de' },
            { value: 'en', textKey: 'i18n.base.shared.Language.en' },
          ],
        },
      },
      enumColumn<UserInfoDTO>('roles', 'i18n.base.user-management.column-headers.role', {
        enumType: Role,
        enumName: 'Role',
        excludeValues: [Role.None]
      }),
      booleanColumn<UserInfoDTO>('enabled', 'i18n.base.user-management.column-headers.enabled'),
    ],
  },
  form: {
    fields: [
      {
        key: 'userId',
        labelKey: 'i18n.base.user-add-modal.userId',
        control: 'text',
        hiddenOnAdd: true,
        hiddenOnEdit: true,
      },
      {
        key: 'firstname',
        labelKey: 'i18n.base.user-add-modal.firstname',
        control: 'text',
        validators: [Validators.required],
        colMd: 6,
      },
      {
        key: 'lastname',
        labelKey: 'i18n.base.user-add-modal.lastname',
        control: 'text',
        validators: [Validators.required],
        colMd: 6,
      },
      {
        key: 'email',
        labelKey: 'i18n.base.user-add-modal.email',
        control: 'email',
        validators: [Validators.required, Validators.email],
        colMd: 12,
      },
      {
        key: 'clientId',
        labelKey: 'i18n.base.user-management.column-headers.client',
        control: 'reference-select',
        validators: [Validators.required],
        referenceConfig: {
          serviceType: ClientService,
          searchField: 'Name',
          textField: 'name',
          valueField: 'id',
          initialValueMapping: {
            sourceField: 'client',
          },
        },
        colMd: 6,
      },
      {
        key: 'language',
        labelKey: 'i18n.base.user-add-modal.language',
        control: 'enum-select',
        validators: [Validators.required],
        options: [
          { value: 'de', textKey: 'i18n.base.shared.Language.de' },
          { value: 'en', textKey: 'i18n.base.shared.Language.en' },
        ],
        colMd: 6,
      },
      {
        key: 'phoneNumber',
        labelKey: 'i18n.base.user-add-modal.phoneNumber',
        control: 'text',
        validators: [CustomValidatorsFactory.createPhoneValidator()],
        colMd: 12,
      },
      {
        key: 'role',
        labelKey: 'i18n.base.user-add-modal.role',
        control: 'custom',
        validators: [Validators.required],
        colMd: 12,
      },
      {
        key: 'enabled',
        labelKey: 'i18n.base.user-add-modal.enabled',
        control: 'checkbox',
        defaultValue: false,
        hiddenOnAdd: true,
        colMd: 12,
      },
    ],
    layout: [
      { fields: ['firstname', 'lastname'] },
      { fields: ['email'] },
      { fields: ['clientId', 'language'] },
      { fields: ['phoneNumber'] },
      { fields: ['role'] },
      { fields: ['enabled'] },
    ],
  },
  mapToCreate: (formValue: UserEditModel): CreateUserDTO => {
    return new CreateUserDTO({
      firstname: formValue.firstname!,
      lastname: formValue.lastname!,
      email: formValue.email!,
      clientId: formValue.clientId ?? undefined,
      language: formValue.language ?? undefined,
      phoneNumber: formValue.phoneNumber ?? undefined,
      roles: [formValue.role!],
    });
  },
  mapToUpdate: (formValue: UserEditModel): UserEditModel => {
    return new UpdateUserInfoDTO({
      userId: formValue.userId,
      firstname: formValue.firstname,
      lastname: formValue.lastname,
      email: formValue.email,
      clientId: formValue.clientId ?? undefined,
      language: formValue.language,
      phoneNumber: formValue.phoneNumber,
      enabled: formValue.enabled,
      roles: [formValue.role!],
      accessCodeValidUntil: formValue.accessCodeValidUntil,
    });
  },
});
